import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const SIGNED_URL_EXPIRY_SEC = 3600;
const STALE_TIME_MS = (SIGNED_URL_EXPIRY_SEC - 600) * 1000; // 50 min

const SIZE_CLASSES = {
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-20 w-20 text-lg",
} as const;

/** Stable background colour from string hash (e.g. fullName or userId). */
function getInitialsColor(str: string): string {
  if (!str) return "bg-muted";
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  const colors = [
    "bg-blue-500/80 text-white",
    "bg-emerald-500/80 text-white",
    "bg-violet-500/80 text-white",
    "bg-amber-500/80 text-white",
    "bg-rose-500/80 text-white",
    "bg-cyan-500/80 text-white",
    "bg-orange-500/80 text-white",
    "bg-teal-500/80 text-white",
  ];
  return colors[Math.abs(h) % colors.length];
}

/** Two letters for initials: first+last name, or first two of name/email. */
function getInitials(fullName?: string | null, userId?: string): string {
  const name = (fullName ?? "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    if (parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
    return parts[0][0].toUpperCase();
  }
  if (userId) {
    return userId.slice(0, 2).toUpperCase();
  }
  return "?";
}

export interface UserAvatarProps {
  userId?: string;
  avatarUrl?: string | null;
  fullName?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function UserAvatar({ userId, avatarUrl, fullName, size = "md", className }: UserAvatarProps) {
  const hasPath = !!avatarUrl?.trim();

  const { data: signedUrl, isLoading, isError } = useQuery({
    queryKey: ["avatar-signed-url", avatarUrl],
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from("avatars")
        .createSignedUrl(avatarUrl!, SIGNED_URL_EXPIRY_SEC);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
    enabled: hasPath,
    staleTime: STALE_TIME_MS,
  });

  const sizeClass = SIZE_CLASSES[size];
  const initials = getInitials(fullName, userId);
  const fallbackBg = getInitialsColor((fullName ?? userId ?? "").trim() || "?");

  if (hasPath && isLoading) {
    return (
      <Skeleton
        className={cn("rounded-full shrink-0", sizeClass, className)}
        aria-hidden
      />
    );
  }

  return (
    <Avatar className={cn("shrink-0", sizeClass, className)}>
      {hasPath && signedUrl && !isError ? (
        <AvatarImage src={signedUrl} alt="" />
      ) : null}
      <AvatarFallback className={cn("rounded-full", fallbackBg)}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
